import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spinner } from '../components/ui';
import { api } from '../lib/api';
import { useT } from '../lib/i18n';
import { errorMessage, useToast } from '../lib/toast';
import type { BackupStatus } from '../lib/types';

/**
 * Where Google's consent screen sends the browser back to.
 *
 * The code and state go straight to the API through the panel's proxy, so
 * finishing the connection needs the operator's session like any other admin
 * call. Then back to the Backups page — the code is single-use, so the url
 * that carried it is replaced rather than left in history.
 */
export function BackupDriveCallbackPage() {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  // StrictMode runs effects twice in development; the code works only once.
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const done = () => void navigate('/backups', { replace: true });
    const error = params.get('error');
    const code = params.get('code');
    const state = params.get('state');

    if (error) {
      if (error === 'access_denied') toast.info(t('backups.callback.cancelled'));
      else toast.error(t('backups.callback.failed', { reason: error }));
      done();
      return;
    }
    if (!code || !state) {
      toast.error(t('backups.callback.missing'));
      done();
      return;
    }

    api
      .post<BackupStatus>('/api/backups/drive/callback', { code, state })
      .then((status) => {
        queryClient.setQueryData(['backups', 'status'], status);
        toast.success(
          t('backups.callback.connected', {
            account: status.drive.accountEmail ?? '',
          }),
        );
      })
      .catch((err: unknown) => toast.error(errorMessage(err)))
      .finally(done);
  }, [params, navigate, queryClient, toast, t]);

  return (
    <div className="grid place-items-center gap-3 py-24 text-center">
      <Spinner className="size-6" />
      <p className="text-sm text-ink-2">{t('backups.callback.working')}</p>
    </div>
  );
}
